/**
 * 거래소 관리자 계약(`~/dev1/sjo-exchange-backend/docs/10_API계약.md` 5절)의 창구들.
 * 경로와 응답 타입을 한 곳에 모은다. 줄마다 계약ID 를 단다.
 *
 * 이 웹이 부르는 것만 있다(명세 1-3 표). 비워 둔 것(XM-45·XM-46, XM-54·XM-55,
 * XM-69·XM-70, XM-72~XM-75, XM-90~XM-92)과 부르지 않는 것(XM-31·XM-35·XM-57)은 없다.
 * 직원 OTP(XM-02 로그인 코드, XM-09~XM-12 등록·풀기)는 정할 것 39(사장님 2026-10-13 「관리자 웹에 직원 OTP가
 * 왜 있냐」)로 없앴다 — 부르지 않는다.
 * 정원 변경 승인(XM-50~XM-52)은 부르지 않는다 — 단계 올리기는 자동, 감액은 없다(R-XAD-02, 원문 3·확인 30).
 *
 * ⚠ **인증은 창구마다 다르지 않다.** 세션 + 관리자 `ACTIVE` + 이 세션에서 PIN 통과 +
 * 버튼 키(7절)를 서버가 본다. 화면이 키를 보고 버튼을 숨기는 것은 편의이고, 최종 판단은
 * 서버다(명세 0-5).
 *
 * ⚠ **금액은 내부 단위 정수 문자열 그대로 주고받는다**(계약 0-4). 여기서 숫자로 바꾸지 않는다.
 */
import { api, apiBlobUrl, query } from './client'
import type {
  AdminMe, AdminSession, AuditPage, CreatedStaff, GradeHistoryRow,
  IncidentAction, IncidentCase, IncidentDetail, IncidentStatus, MemberDetail, MemberGrade,
  MemberKyc, MemberRisk, MemberRow, Paged, PartnerApplication, PartnerApproved, PartnerDetail,
  PartnerRow, PartnerTerms, PermissionNameInput, PermissionNameRow, PermissionPage,
  PermissionNameRef, AdminStatus, Revocation, RevocationDetail, RevocationInput, RiskQueueEntry,
  RiskQueueItem, RiskQueuePatch, RiskRule, RiskRulePatch, Setting, SignupApplication, StaffRow, KycDocuments,
  TradeView, Withdrawal, Millis, RetentionPolicy, RetentionRun, UserLogArchive, PartnerTier,
  FeeMonth, TermsKind, ArchiveOverview, ArchiveRun, ArchivedRows, WalletTradeTerms, MaintenanceState,
  CustomerCenterGuide, TradeMaterialMailTemplate, IncidentMaterialMail, MaterialMailRole, IncidentDocument,
  MaterialMailPreview, AccountMailTemplate, MemberStatus, PendingSuspension, PenaltyLock,
} from './types'

/**
 * ⚠ **`| undefined` 를 명시한다.** `exactOptionalPropertyTypes` 아래에서는
 * 「생략해도 된다」와 「undefined 를 넣어도 된다」가 다른 말이다. 거르개 값은
 * 「안 고름」일 때 undefined 로 오므로 뒤엣것이 필요하다.
 */
type Opt<T> = T | undefined

/** `offset`·`limit` 쪽 나누기 (계약 0-6 — 기본 50, 1~200). */
export interface OffsetParams {
  offset?: Opt<number>
  limit?: Opt<number>
}

/** `page`·`size` 쪽 나누기 (계약 0-6 — page ≥ 0, size 1~200). */
export interface PageParams {
  page?: Opt<number>
  size?: Opt<number>
}

const seg = (v: string | number) => encodeURIComponent(String(v))

/* ── 5-1 로그인·자기 정보 ─────────────────────────────────────────── */

/**
 * XM-01 — 로그인. 첫 로그인은 메일로 받은 임시 비밀번호.
 *
 * `force` — 정할 것 45(사장님 45번 「… 예 누르면 이전세션 로그아웃 시키고 접속하면 1계정만 로그인되는거잖아」): 같은 계정이 이미
 * 로그인 중이라 409 `already_signed_in` 을 받은 뒤 팝업의 [예]. 서버가 그 계정의 다른 세션을 끝내고 새 세션을 준다(계약 XM-01
 * 2026-10-13). 보통 로그인은 전과 같은 몸(`force` 를 싣지 않음).
 */
export const login = (loginId: string, password: string, force?: boolean) =>
  api<AdminSession>('/admin/auth/login', {
    method: 'POST', body: force ? { loginId, password, force: true } : { loginId, password }, anonymous: true,
  })

/** XM-03 — 처음 설정(새 비밀번호 + PIN). 끝나면 이 세션은 PIN 을 통과한 것으로 본다. */
export const firstSetup = (newPassword: string, newPin: string) =>
  api<{ done: boolean }>('/admin/auth/first-setup', {
    method: 'POST', body: { newPassword, newPin },
  })

/** XM-04 — PIN 관문. */
export const verifyPin = (pin: string) =>
  api<{ pinVerified: boolean }>('/admin/auth/pin', { method: 'POST', body: { pin } })

/** XM-05 — 로그아웃. ⚠ 서버는 **그 계정의 모든 세션**을 끊는다. */
export const logout = () => api<{ loggedOut: boolean }>('/admin/auth/logout', { method: 'POST' })

/** XM-06 — 자기 정보. 메뉴·버튼을 그리는 근거(`superAdmin`·`keys`). PIN 앞에서도 준다. */
export const fetchMe = () => api<AdminMe>('/admin/me')

/** XM-07 — 내 PIN 바꾸기. 바꿔도 이 세션의 PIN 통과는 그대로다. */
export const changePin = (currentPin: string, newPin: string) =>
  api<{ changed: boolean }>('/admin/me/pin', { method: 'PUT', body: { currentPin, newPin } })

/** XM-08 — 내 비밀번호 바꾸기. ⚠ 세션을 끊지 않는다 — 화면도 로그아웃시키지 않는다. */
export const changePassword = (currentPassword: string, newPassword: string) =>
  api<{ changed: boolean }>('/admin/me/password', {
    method: 'PUT', body: { currentPassword, newPassword },
  })

/* ── 5-1 직원·권한 이름 (최고관리자만) ───────────────────────────── */

/** XM-13 — 직원 목록. */
export const listStaff = () => api<{ admins: StaffRow[] }>('/admin/admins')

/** XM-14 — 직원 만들기. 임시 비밀번호는 서버가 메일로만 보낸다. */
export const createStaff = (loginId: string, email: string, permissionNameId: number) =>
  api<CreatedStaff>('/admin/admins', {
    method: 'POST', body: { loginId, email, permissionNameId },
  })

/** XM-15 — 직원 상태. */
export const setStaffStatus = (adminId: number, status: AdminStatus) =>
  api<{ adminId: number; status: AdminStatus }>(`/admin/admins/${seg(adminId)}/status`, {
    method: 'PUT', body: { status },
  })

/** XM-16 — 직원의 권한 이름. 다음 요청부터 바로 적용된다. */
export const setStaffPermissionName = (adminId: number, permissionNameId: number) =>
  api<{ adminId: number; permissionName: PermissionNameRef }>(
    `/admin/admins/${seg(adminId)}/permission-name`,
    { method: 'PUT', body: { permissionNameId } })

/** XM-17 — 임시 비밀번호 다시 보내기. 그 직원의 세션을 끊고 PIN 을 비운다. */
export const resendTemporaryPassword = (adminId: number) =>
  api<{ sent: boolean }>(`/admin/admins/${seg(adminId)}/temporary-password`, { method: 'POST' })

/**
 * XM-118 — 직원 잠금 풀기(최고관리자만, 2026-10-01 사장님 질문 39 「1」). 로그인 잠금(비밀번호 3번 틀림)과 PIN
 * 잠금을 한 번에 푼다. 상태·비밀번호·PIN·세션은 그대로. 풀 것이 없으면 아무것도 바꾸지 않는다(`unlocked` false).
 */
export const unlockStaff = (adminId: number) =>
  api<{ adminId: number; unlocked: boolean; loginLocked: boolean; pinLocked: boolean }>(
    `/admin/admins/${seg(adminId)}/unlock`, { method: 'POST' })

/** XM-18 — 고를 수 있는 페이지·버튼(7절 표 그대로). */
export const listPermissionPages = () => api<{ pages: PermissionPage[] }>('/admin/permissions')

/** XM-19 — 권한 이름 목록. */
export const listPermissionNames = () =>
  api<{ names: PermissionNameRow[] }>('/admin/permission-names')

/** XM-20 — 권한 이름 만들기. */
export const createPermissionName = (input: PermissionNameInput) =>
  api<PermissionNameRow>('/admin/permission-names', { method: 'POST', body: input })

/** XM-21 — 권한 이름 고치기. `keys` 는 전체를 덮어쓴다 — 그 이름의 직원 모두에게 바로 적용. */
export const updatePermissionName = (nameId: number, input: PermissionNameInput) =>
  api<PermissionNameRow>(`/admin/permission-names/${seg(nameId)}`, { method: 'PUT', body: input })

/** XM-22 — 권한 이름 지우기(쓰는 직원이 없을 때만). */
export const deletePermissionName = (nameId: number) =>
  api<{ deleted: boolean }>(`/admin/permission-names/${seg(nameId)}`, { method: 'DELETE' })

/* ── 5-2 회원 ─────────────────────────────────────────────────────── */

export interface MemberListParams extends OffsetParams {
  /** 포함 검색. */
  loginId?: Opt<string>
  grade?: Opt<string>
  status?: Opt<string>
  kycStatus?: Opt<string>
  masterId?: Opt<number>
}

/** XM-23 — 회원 목록(offset). */
export const listMembers = (p: MemberListParams = {}) =>
  api<MemberRow[]>('/admin/members' + query({ ...p }))

/** XM-24 — 회원 상세. */
export const getMember = (memberId: number) =>
  api<MemberDetail>(`/admin/members/${seg(memberId)}`)

/** XM-25 — 등급 이력. 거르개가 없다 — 사유 거르기는 받은 목록 안에서. */
export const listGradeHistory = (memberId: number) =>
  api<GradeHistoryRow[]>(`/admin/members/${seg(memberId)}/grade-history`)

/**
 * XM-26 — 회원 정지·해제. 거래 중인 회원의 정지는 곧바로 걸리지 않고 `pendingSuspension` 이 온다(`status` 는 `ACTIVE`) —
 * 그 거래가 완료·취소되면 서버가 정지한다. 걸어 둔 채 `ACTIVE` 를 보내면 걸어 둔 것을 지운다.
 */
export const setMemberStatus = (memberId: number, status: 'ACTIVE' | 'SUSPENDED', reason?: string) =>
  api<{ memberId: number; status: MemberStatus; pendingSuspension: PendingSuspension | null }>(
    `/admin/members/${seg(memberId)}/status`, {
      method: 'POST', body: reason ? { status, reason } : { status },
    })

/**
 * XM-125 — 패널티 잠금 해제(2026-10-29 사장님 앱 수정 D 「관리자가 풀 때까지 잠금」 — 해제 원하면 고객센터 직접 전화). 그 회원의 잠금을
 * 모두 풀고 지갑 서버·거래소 앱에 알린다(감사 기록). 잠기지 않은 회원은 409. 사유는 선택이다.
 */
export const releasePenaltyLock = (memberId: number, reason?: string) =>
  api<PenaltyLock>(`/admin/members/${seg(memberId)}/penalty-unlock`, {
    method: 'POST', body: reason ? { reason } : {},
  })

/** XM-27 — 등급 직접 변경(사유 필수). 스페셜로 가면 소속시킬 마스터 번호가 필요하다. */
export const changeMemberGrade = (
  memberId: number, grade: Exclude<MemberGrade, 'MASTER'>, reason: string, masterId?: number,
) =>
  api<{ memberId: number; grade: MemberGrade; masterId: number | null }>(
    `/admin/members/${seg(memberId)}/grade`,
    { method: 'POST', body: masterId === undefined ? { grade, reason } : { grade, masterId, reason } })

/** XM-28 — 스페셜 퇴출(사유 필수). */
export const expelMember = (memberId: number, reason: string) =>
  api<{ memberId: number; grade: MemberGrade }>(`/admin/members/${seg(memberId)}/expel`, {
    method: 'POST', body: { reason },
  })

/** XM-29 — 회원 KYC 보기. */
export const getMemberKyc = (memberId: number) =>
  api<MemberKyc>(`/admin/members/${seg(memberId)}/kyc`)

/* ── 5-2 가입·파트너 심사 ─────────────────────────────────────────── */

/** XM-30 — 가입 신청 목록(기본 100건, 1~500). 2026-10-12 부터 KYC 인증 메뉴의 목록. */
export const listSignups = (p: { status?: Opt<string>; limit?: Opt<number> } = {}) =>
  api<{ applications: SignupApplication[] }>('/admin/signup-applications' + query({ ...p }))

/** XM-31 — 가입 신청 하나(신원 인증 전부 + SUMSUB 결과). */
export const getSignup = (applicationId: number) =>
  api<SignupApplication>(`/admin/signup-applications/${seg(applicationId)}`)

/** XM-123 — 신청자가 SUMSUB 에 낸 단계와 사진 번호(이 서버는 남기지 않고 그때 SUMSUB 에 묻는다). */
export const getSignupDocuments = (applicationId: number) =>
  api<KycDocuments>(`/admin/signup-applications/${seg(applicationId)}/kyc-documents`)

/** XM-124 — 사진 하나(인증 헤더가 필요해 object URL 로 — 호출자가 revoke). 볼 때마다 서버 감사 기록. */
export const fetchSignupDocument = (applicationId: number, imageId: string) =>
  apiBlobUrl(`/admin/signup-applications/${seg(applicationId)}/kyc-documents/${seg(imageId)}`)

/** XM-32 — 가입 승인(본문 없음). 2026-10-12 부터 수동심사(`MANUAL_REVIEW`)만. */
export const approveSignup = (applicationId: number) =>
  api<SignupApplication>(`/admin/signup-applications/${seg(applicationId)}/approve`, {
    method: 'POST',
  })

/** XM-33 — 가입 거절. 사유는 선택이다. 2026-10-12 부터 수동심사만 — 신청과 신원 인증이 닫힌다. */
export const rejectSignup = (applicationId: number, reason?: string) =>
  api<SignupApplication>(`/admin/signup-applications/${seg(applicationId)}/reject`, {
    method: 'POST', ...(reason ? { body: { reason } } : {}),
  })

/** XM-34 — 파트너 신청 목록(offset). */
export const listPartnerApplications = (p: { status?: Opt<string> } & OffsetParams = {}) =>
  api<PartnerApplication[]>('/admin/partner-applications' + query({ ...p }))

/** XM-36 — 파트너 승인(본문 없음). 승인하면 미예치 — 첫 예치 기한이 온다. */
export const approvePartnerApplication = (applicationId: number) =>
  api<PartnerApproved>(`/admin/partner-applications/${seg(applicationId)}/approve`, {
    method: 'POST',
  })

/** XM-37 — 파트너 거절(사유 필수). */
export const rejectPartnerApplication = (applicationId: number, reason: string) =>
  api<PartnerApplication>(`/admin/partner-applications/${seg(applicationId)}/reject`, {
    method: 'POST', body: { reason },
  })

/* ── 5-2 마스터 박탈 ──────────────────────────────────────────────── */

/** XM-38 — 박탈 요청. 대상은 회원 **번호**다. */
export const requestRevocation = (input: RevocationInput) => {
  const body: Record<string, unknown> = { memberId: input.memberId, reason: input.reason }
  if (input.riskReason) body['riskReason'] = input.riskReason
  if (input.relatedIncidentId !== undefined) body['relatedIncidentId'] = input.relatedIncidentId
  if (input.relatedOrderId) body['relatedOrderId'] = input.relatedOrderId
  return api<Revocation>('/admin/master-revocations', { method: 'POST', body })
}

/** XM-39 — 박탈 목록(offset). */
export const listRevocations = (p: { status?: Opt<string> } & OffsetParams = {}) =>
  api<Revocation[]>('/admin/master-revocations' + query({ ...p }))

/** XM-40 — 박탈 상세 + 정산 예상(`settles`·`recomputed`). */
export const getRevocation = (revocationId: number) =>
  api<RevocationDetail>(`/admin/master-revocations/${seg(revocationId)}`)

/** XM-41 — 박탈 승인(본문 없음). ⚠ **돈** — 보증금을 철회처럼 정산한다. */
export const approveRevocation = (revocationId: number) =>
  api<Revocation>(`/admin/master-revocations/${seg(revocationId)}/approve`, { method: 'POST' })

/** XM-42 — 박탈 거부(사유 필수). */
export const rejectRevocation = (revocationId: number, reason: string) =>
  api<Revocation>(`/admin/master-revocations/${seg(revocationId)}/reject`, {
    method: 'POST', body: { reason },
  })

/* ── 5-3 보증금·철회·정원 변경 ────────────────────────────────────── */

/** XM-43 — 파트너 목록(offset). */
export const listPartners = (p: { status?: Opt<string> } & OffsetParams = {}) =>
  api<PartnerRow[]>('/admin/partners' + query({ ...p }))

/** XM-44 — 파트너 보증금 상세(충전·체인 작업 포함). */
export const getPartner = (partnerId: number) =>
  api<PartnerDetail>(`/admin/partners/${seg(partnerId)}`)

/**
 * XM-46(고침 — api-XB) — 월 사용료 기록(월 10,000 USDE 선납, R-XB-06). 옛 `fee-days` 는 부르지 않는다.
 */
export const listFeeMonths = (partnerId: number) =>
  api<FeeMonth[]>(`/admin/partners/${seg(partnerId)}/fee-months`)

/** XM-47 — 철회 목록(offset). `recomputed` 는 목록에서 null. */
export const listWithdrawals = (p: { status?: Opt<string> } & OffsetParams = {}) =>
  api<Withdrawal[]>('/admin/partner-withdrawals' + query({ ...p }))

/** XM-48 — 철회 한 건. `recomputed` = 지금 잔액으로 다시 계산(체인을 못 읽으면 null). */
export const getWithdrawal = (withdrawalId: number) =>
  api<Withdrawal>(`/admin/partner-withdrawals/${seg(withdrawalId)}`)

/** XM-49 — 철회 승인(본문 없음). ⚠ **돈** — 위약금·사용료·반환액을 정산한다. */
export const approveWithdrawal = (withdrawalId: number) =>
  api<Withdrawal>(`/admin/partner-withdrawals/${seg(withdrawalId)}/approve`, { method: 'POST' })

/* ── 5-4 거래·사고 ────────────────────────────────────────────────── */

export interface TradeListParams extends PageParams {
  from?: Opt<Millis>
  to?: Opt<Millis>
  stage?: Opt<string>
  memberId?: Opt<number>
  address?: Opt<string>
  orderId?: Opt<string>
}

/** XM-53 — 거래 목록(page). */
export const listTrades = (p: TradeListParams = {}) =>
  api<Paged<TradeView>>('/admin/trades' + query({ ...p }))

export interface IncidentListParams extends PageParams {
  status?: Opt<IncidentStatus>
  /** 검색 칸 하나 — 거래 번호·주문 번호·판매자 또는 구매자 아이디·지갑 주소(글자가 같은 것). */
  q?: Opt<string>
  /** 거래 시각(매칭 시각) 기간 — 밀리초, `to` 미포함. */
  from?: Opt<Millis>
  to?: Opt<Millis>
}

/**
 * XM-56 — 사고 목록(page). 상태는 하나만 거른다. 2026-10-01 새 쿼리(거래소백엔드 da01075): `q`(검색 칸)·`from`·`to`
 * (거래 시각 기간 — 관리자 컴퓨터 시간의 날짜 경계를 밀리초로). `from` 이 `to` 보다 늦거나 같으면 서버가 400.
 */
export const listIncidents = (p: IncidentListParams = {}) =>
  api<Paged<IncidentCase>>('/admin/incidents' + query({ ...p }))

/**
 * XM-58 — 사고 상세 열람(두 번째 문).
 *
 * ⚠ **여는 것 자체가 기록이다.** 열람마다 감사 기록이 남으므로 **버튼으로만** 부른다 —
 * useQuery 에 넣지 않는다(레거시 그대로).
 */
export const openIncident = (id: number, reason: string, password: string) =>
  api<IncidentDetail>(`/admin/incidents/${seg(id)}/detail`, {
    method: 'POST', body: { reason, password },
  })

/** XM-59 — 증빙 사진 한 장을 object URL 로. 호출자가 revoke 를 맡는다. */
export const fetchProofPhoto = (id: number, proofId: number) =>
  apiBlobUrl(`/admin/incidents/${seg(id)}/proofs/${seg(proofId)}/photo`)

/**
 * XM-116 — 사고에 올린 문서(공문 사진·PDF) 한 장을 object URL 로. 호출자가 revoke 를 맡는다. 볼 때마다 서버가
 * 사고 이력 `DOCUMENT_VIEWED`·감사 기록을 남긴다(2026-10-01 — 거래소백엔드 999cd8f).
 */
export const fetchIncidentDocument = (id: number, documentId: number) =>
  apiBlobUrl(`/admin/incidents/${seg(id)}/documents/${seg(documentId)}`)

/** XM-60 — 외부 인계. 사고를 닫지 않고 기록만 남긴다. */
export const handOverIncident = (id: number, handoverTo: string, note?: string) =>
  api<IncidentCase>(`/admin/incidents/${seg(id)}/handover`, {
    method: 'POST', body: note ? { handoverTo, note } : { handoverTo },
  })

/**
 * 처리 요청에 붙이는 오프라인 증빙(2026-10-01 사장님 질문 30 「1」·「2」 — 모든 칸 필수, 거래소백엔드 999cd8f):
 * 확인한 기관(≤200)·담당자(≤100)·확인 날짜(`yyyy-mm-dd`)·문서번호(≤100)·문서 파일(XM-115 로 이 사고에 올린 문서 번호).
 */
export interface OfflineEvidence {
  institution: string
  officer: string
  confirmedOn: string
  documentNo: string
  documentId: number
}

/**
 * XM-61 — 사고 처리. ⚠ **돈** — 에스크로에 처리를 보낸다. 결과는 체인 확인 뒤. `action` 은
 * `CONFIRM`(거래완료)·`CANCEL`(거래취소) 둘만(2026-10-01 거래 흐름 04 9단계 — 그 밖은 서버 400). 처리 근거와
 * 오프라인 증빙 다섯 칸 모두 필수 — 하나라도 비면 서버가 400(`bad_request`·`evidence_required` + `reasonCode` 칸 이름).
 */
export const actOnIncident = (id: number, action: IncidentAction, basis: string, evidence: OfflineEvidence) =>
  api<IncidentCase>(`/admin/incidents/${seg(id)}/actions`, {
    method: 'POST',
    body: {
      action, basis,
      institution: evidence.institution, officer: evidence.officer, confirmedOn: evidence.confirmedOn,
      documentNo: evidence.documentNo, documentId: evidence.documentId,
    },
  })

/**
 * XM-115 — 처리에 쓸 문서 파일(확인한 공문의 사진·PDF — 서버가 내용으로 JPEG·PNG·PDF 를 가림, 8MB 이하)을 이 사고에
 * 올린다(multipart 칸 `file`). 답의 `id` 를 XM-61 `documentId` 로 쓴다. 부를 때마다 새 문서이고 지우는 창구는 없다
 * (사고와 함께 보존 — 2026-10-01, 거래소백엔드 999cd8f). 끝난 사고는 409.
 */
export const uploadIncidentDocument = (id: number, file: File) => {
  const form = new FormData()
  form.append('file', file, file.name)
  return api<IncidentDocument>(`/admin/incidents/${seg(id)}/documents`, { method: 'POST', form })
}

/**
 * XM-117 — [자료 메일 보내기] 창 채우기(키 `incidents.handle`, 2026-10-01 — 거래소백엔드 5df107e). 받는 사람마다 템플릿
 * 제목·본문(에디터), 붙을 거래 자료(고칠 수 없음), 붙일 그 회원 자기 사진. 아무것도 보내거나 적지 않는다. 2026-10-02 부터
 * 영어로만 — 쿼리 `language` 없음(거래소백엔드 1ab5d45). 가입 이메일이 없으면 409 `email_missing`, 템플릿이 없으면 409
 * `mail_template_missing`.
 */
export const previewIncidentMaterialMail = (id: number, role: MaterialMailRole) =>
  api<MaterialMailPreview>(`/admin/incidents/${seg(id)}/material-mails/preview` + query({ role }))

/**
 * XM-114 — 거래 자료 메일 보내기(키 `incidents.handle`). 사고 거래의 한 당사자(`seller`·`buyer`)의 가입 이메일로,
 * 관리자가 창에서 고친 제목·본문(XM-117 로 채운 것) + 서버가 DB 에서 다시 만든 거래 자료 + 그 회원 자신의 증빙 사진을
 * 보낸다(2026-10-01 질문 23 — 거래소백엔드 5df107e). 2026-10-02 부터 영어로만 — `language` 칸 없음, 한글이 있으면 400
 * `korean_not_allowed`(거래소백엔드 1ab5d45). 답은 보낸 기록. 부를 때마다 새 메일·새 기록이다(실패도 `FAILED` 로 남고
 * 503). `mail` 을 빼면 서버가 템플릿 글 그대로 보낸다.
 */
export const sendIncidentMaterialMail = (
  id: number, role: MaterialMailRole, mail?: { subject: string; body: string },
) =>
  api<IncidentMaterialMail>(`/admin/incidents/${seg(id)}/material-mails`, {
    method: 'POST',
    body: mail ? { role, subject: mail.subject, body: mail.body } : { role },
  })

/* ── 5-4 위험 ─────────────────────────────────────────────────────── */

/** XM-62 — 위험 규칙. */
export const listRiskRules = () => api<RiskRule[]>('/admin/risk/rules')

/** XM-63 — 규칙 값 바꾸기(바뀐 칸만). */
export const patchRiskRule = (code: string, patch: RiskRulePatch) =>
  api<RiskRule>(`/admin/risk/rules/${seg(code)}`, { method: 'PATCH', body: patch })

/** XM-64 — 회원 위험. */
export const getMemberRisk = (memberId: number) =>
  api<MemberRisk>(`/admin/risk/members/${seg(memberId)}`)

/** XM-65 — 회원 위험 다시 평가. */
export const evaluateMemberRisk = (memberId: number) =>
  api<{ createdItemIds: number[] }>(`/admin/risk/members/${seg(memberId)}/evaluate`, {
    method: 'POST',
  })

export interface RiskQueueParams extends PageParams {
  status?: Opt<string>
  level?: Opt<string>
  memberId?: Opt<number>
  assigneeAdminId?: Opt<number>
}

/** XM-66 — 이상거래 큐(page). */
export const listRiskQueue = (p: RiskQueueParams = {}) =>
  api<Paged<RiskQueueItem>>('/admin/risk/queue' + query({ ...p }))

/** XM-67 — 이상거래 항목 상세. */
export const getRiskQueueItem = (id: number) =>
  api<RiskQueueEntry>(`/admin/risk/queue/${seg(id)}`)

/** XM-68 — 이상거래 항목 상태·담당·메모(바뀐 칸만). */
export const patchRiskQueueItem = (id: number, patch: RiskQueuePatch) =>
  api<RiskQueueEntry>(`/admin/risk/queue/${seg(id)}`, { method: 'PATCH', body: patch })

/* ── 5-5 감사 기록·설정·약관 ──────────────────────────────────────── */

export interface AuditParams extends PageParams {
  adminId?: Opt<number>
  relatedIncidentId?: Opt<number>
  category?: Opt<string>
  action?: Opt<string>
  targetType?: Opt<string>
  targetId?: Opt<string>
  targetMemberLoginId?: Opt<string>
  relatedOrderId?: Opt<string>
  /** 밀리초. to 는 포함하지 않는다. */
  from?: Opt<Millis>
  to?: Opt<Millis>
}

/** XM-71 — 감사 기록(page). 범위는 서버가 권한 이름의 `auditScope` 로 건다. */
export const listAuditLogs = (p: AuditParams = {}) =>
  api<AuditPage>('/admin/audit-logs' + query({ ...p }))

/** XM-83 — 시스템 설정 목록. */
export const listSettings = () => api<Setting[]>('/admin/settings')

/** XM-84 — 설정 한 건. */
export const getSetting = (key: string) => api<Setting>(`/admin/settings/${seg(key)}`)

/**
 * XM-85 — 설정 바꾸기. 값은 서버가 저장하는 **문자열 그대로**다(USDE 는 내부 단위).
 * ⚠ **돈 기준**일 수 있다 — 주문 최소·최대, 거래 범위, 사용료.
 */
export const putSetting = (key: string, value: string | null) =>
  api<Setting>(`/admin/settings/${seg(key)}`, { method: 'PUT', body: { value } })

/*
 * 약관(XM-86~XM-89). 고침(api-XB — R-XB-24·R-XAD-05): 쿼리·본문 `kind` = `PARTNER`(기본)·
 * `WITHDRAWAL`(철회 약관 — 확인 36). `kind` 를 주지 않으면 서버가 파트너 약관으로 읽는다.
 */

/** XM-86 — 약관 판 목록. */
export const listPartnerTerms = (kind?: TermsKind) =>
  api<PartnerTerms[]>('/admin/partner-terms' + query({ kind }))

/** XM-87 — 시행 중인 약관(없으면 404). */
export const getEffectivePartnerTerms = (kind?: TermsKind) =>
  api<PartnerTerms>('/admin/partner-terms/effective' + query({ kind }))

/** XM-88 — 약관 한 판. */
export const getPartnerTerms = (version: number, kind?: TermsKind) =>
  api<PartnerTerms>(`/admin/partner-terms/${seg(version)}` + query({ kind }))

/**
 * XM-89 — 약관 새 판. 옛 판은 지우지 않는다. 시행일이 없으면 지금. `korean` — 시연용 한국어 제목·본문(2026-10-12,
 * 거래소백엔드 V544 — 둘 다 있어야 한다, 하나만이면 400 `bad_request`). 없으면 싣지 않는다(영어만인 판).
 */
export const createPartnerTerms = (
  title: string, body: string, effectiveAt?: Millis, kind?: TermsKind,
  korean?: { titleKo: string; bodyKo: string },
) =>
  api<PartnerTerms>('/admin/partner-terms', {
    method: 'POST',
    body: {
      title, body,
      ...(effectiveAt === undefined ? {} : { effectiveAt }),
      ...(kind === undefined ? {} : { kind }),
      ...(korean === undefined ? {} : { titleKo: korean.titleKo, bodyKo: korean.bodyKo }),
    },
  })

/*
 * 지갑거래서명약관(XM-100~XM-103 — 거래 흐름 04 3-3, 거래소백엔드 `WalletTradeTermsController`).
 * 지갑 앱이 거래 서명 전에 보여 주는 약관. 판마다 한국어·영어 두 글이다. 키는 약관 키 그대로
 * (`terms.view`·`terms.edit`). 지갑은 지갑백엔드 → XW-15 로 시행 중인 판을 받아 간다.
 */

/** XM-100 — 지갑거래서명약관 판 목록(새 판 먼저). */
export const listWalletTradeTerms = () => api<WalletTradeTerms[]>('/admin/wallet-trade-terms')

/** XM-101 — 시행 중인 판 = 지갑이 지금 받는 판(없으면 404). */
export const getEffectiveWalletTradeTerms = () => api<WalletTradeTerms>('/admin/wallet-trade-terms/effective')

/** XM-102 — 한 판. */
export const getWalletTradeTerms = (version: number) =>
  api<WalletTradeTerms>(`/admin/wallet-trade-terms/${seg(version)}`)

/** XM-103 — 새 판. 네 칸 모두 필수, 옛 판은 지우지 않는다. 시행일이 없으면 지금. */
export const createWalletTradeTerms = (
  text: { titleKo: string; bodyKo: string; titleEn: string; bodyEn: string },
  effectiveAt?: Millis,
) =>
  api<WalletTradeTerms>('/admin/wallet-trade-terms', {
    method: 'POST',
    body: {
      titleKo: text.titleKo, bodyKo: text.bodyKo, titleEn: text.titleEn, bodyEn: text.bodyEn,
      ...(effectiveAt === undefined ? {} : { effectiveAt }),
    },
  })

/*
 * 고객센터 안내(XM-106~XM-109 — 거래 흐름 04 8단계, 거래소 01 6-35, 거래소백엔드
 * `CustomerCenterGuideController`). 사장님 2026-10-01 「거래소 관리자 약관처럼 거래소관리자에서 관리 할 수
 * 있도록 만들어라」. 판마다 한국어·영어 연락처·운영 시간·안내 글. 키는 약관 키 그대로(`terms.view`·
 * `terms.edit`). 거래소 앱은 XA-69 로 시행 중인 판을 받는다(이 웹은 그 길에 없음).
 */

/** XM-106 — 고객센터 안내 판 목록(새 판 먼저, 없으면 빈 배열). */
export const listCustomerCenterGuides = () => api<CustomerCenterGuide[]>('/admin/customer-center-guides')

/** XM-107 — 시행 중인 판 = 앱이 지금 받는 판(없으면 404). */
export const getEffectiveCustomerCenterGuide = () =>
  api<CustomerCenterGuide>('/admin/customer-center-guides/effective')

/** XM-108 — 한 판. */
export const getCustomerCenterGuide = (version: number) =>
  api<CustomerCenterGuide>(`/admin/customer-center-guides/${seg(version)}`)

/** XM-109 — 새 판. 여섯 칸 모두 필수, 옛 판은 지우지 않는다. 시행일이 없으면 지금. */
export const createCustomerCenterGuide = (
  text: { contactKo: string; hoursKo: string; bodyKo: string; contactEn: string; hoursEn: string; bodyEn: string },
  effectiveAt?: Millis,
) =>
  api<CustomerCenterGuide>('/admin/customer-center-guides', {
    method: 'POST',
    body: {
      contactKo: text.contactKo, hoursKo: text.hoursKo, bodyKo: text.bodyKo,
      contactEn: text.contactEn, hoursEn: text.hoursEn, bodyEn: text.bodyEn,
      ...(effectiveAt === undefined ? {} : { effectiveAt }),
    },
  })

/*
 * 거래 자료 메일 템플릿(XM-110~XM-113 — 거래 흐름 04 8단계, 거래소 01 6-36, 거래소백엔드
 * `TradeMaterialMailTemplateController`). 사고 상세의 자료 메일(XM-117·XM-114)이 시행 중인 판을 쓴다. 같은 키
 * (`terms.view`·`terms.edit`). 2026-10-02 부터 한 판 = 영어 한 벌(제목·본문 — 사장님 「일단 영어로만」, 거래소백엔드
 * 1ab5d45; 2026-10-01 의 한국어·영어 두 벌은 없어짐). 서버가 영어 거래 자료를 본문 뒤에 붙인다.
 */

/** XM-110 — 템플릿 판 목록(새 판 먼저, 없으면 빈 배열). */
export const listTradeMaterialMailTemplates = () =>
  api<TradeMaterialMailTemplate[]>('/admin/trade-material-mail-templates')

/** XM-111 — 시행 중인 판 = 지금 보내면 쓰는 판(없으면 404). */
export const getEffectiveTradeMaterialMailTemplate = () =>
  api<TradeMaterialMailTemplate>('/admin/trade-material-mail-templates/effective')

/** XM-112 — 한 판. */
export const getTradeMaterialMailTemplate = (version: number) =>
  api<TradeMaterialMailTemplate>(`/admin/trade-material-mail-templates/${seg(version)}`)

/**
 * XM-113 — 새 판 `{subject, body, effectiveAt?}`(영어 한 벌 — 2026-10-02, 거래소백엔드 1ab5d45). 둘 다 필수, 한글이 있으면
 * 400 `korean_not_allowed`. 옛 판은 지우지 않는다. 시행일이 없으면 지금.
 */
export const createTradeMaterialMailTemplate = (
  text: { subject: string; body: string },
  effectiveAt?: Millis,
) =>
  api<TradeMaterialMailTemplate>('/admin/trade-material-mail-templates', {
    method: 'POST',
    body: { subject: text.subject, body: text.body, ...(effectiveAt === undefined ? {} : { effectiveAt }) },
  })

/*
 * 자동 메일 틀(XM-119~XM-122 — 거래소 01 6-37, 거래소백엔드 `AccountMailTemplateController` bb11762). 사장님 2026-10-02
 * 「통상적으로는 관리자가 메일 다섯가지에 대해서 관리 할 수 있게 하는가」 → 「1」: 회원에게 저절로 나가는 메일(인증번호 셋·
 * 임시 비밀번호·가입 결과)의 제목·본문을 판으로 관리한다. 영어 한 벌, 값은 서버가 본문 아래 고정 줄로 붙인다. 키는 다른
 * 틀과 같다(`terms.view`·`terms.edit`). 고치기·지우기는 없다(새 판만).
 */

/** XM-119 — 판 목록(종류 차례, 그 안에서 새 판 먼저). `kind` 를 주면 그 종류만. */
export const listAccountMailTemplates = (kind?: string) =>
  api<AccountMailTemplate[]>('/admin/account-mail-templates' + query({ kind }))

/** XM-120 — 종류마다 시행 중인 판 하나(= 지금 보내면 쓰는 판, 1판이 있어 늘 여섯 줄). */
export const getEffectiveAccountMailTemplates = () =>
  api<AccountMailTemplate[]>('/admin/account-mail-templates/effective')

/** XM-121 — 한 종류의 한 판. */
export const getAccountMailTemplate = (kind: string, version: number) =>
  api<AccountMailTemplate>(`/admin/account-mail-templates/${seg(kind)}/${seg(version)}`)

/**
 * XM-122 — 새 판(그 종류의 가장 새 판 + 1). 제목 ≤200·본문 ≤20000, 둘 다 필수, 한글이 있으면 서버도 400. 시행 시각이
 * 없으면 지금. 옛 판은 지우지 않는다.
 */
export const createAccountMailTemplate = (
  kind: string,
  text: { subject: string; body: string },
  effectiveAt?: Millis,
) =>
  api<AccountMailTemplate>('/admin/account-mail-templates', {
    method: 'POST',
    body: { kind, subject: text.subject, body: text.body, ...(effectiveAt === undefined ? {} : { effectiveAt }) },
  })

/*
 * 서버 점검(XM-104·XM-105 — 거래소 01 6-34, 거래소백엔드 `MaintenanceController`). 사장님 2026-10-01
 * 「거래소 관리자에 서버 점검 버튼만들어서 서버 점검 상태를 만들어서 테스트 하면되잖아」. 키는 다른
 * 시스템 설정과 같다(`settings.view`·`settings.edit`). 관리자 창구는 점검 중에도 답한다.
 */

/** XM-104 — 서버 점검·거래소 점검 상태. */
export const getMaintenance = () => api<MaintenanceState>('/admin/maintenance')

/**
 * XM-105 — 서버 점검 켜기(true)·끄기(false). 답은 바꾼 뒤 상태(XM-104 모양). 같은 값을 다시 보내면
 * 아무것도 바꾸지 않는다. 끄기는 서버 점검만 닫는다 — USDE 멈춤·체인 장애 점검은 그대로.
 */
export const setMaintenance = (maintenance: boolean) =>
  api<MaintenanceState>('/admin/maintenance', { method: 'PUT', body: { maintenance } })

/* ── 보증금 단계 표 (XM-93~XM-95 — api-XB, R-XB-01·R-XAD-01) ──────── */

/** XM-93 — 단계 표(은퇴한 줄 포함). 키 `settings.view`. */
export const listPartnerTiers = () =>
  api<{ tiers: PartnerTier[] }>('/admin/partner-tiers')

/**
 * XM-94 — 단계 줄 더하기. 키 `settings.edit`. ⚠ **돈 기준** — 새 신청·단계 올리기의 설정 금액.
 * 금액은 내부 단위 문자열 그대로(1 USDE = 100).
 */
export const createPartnerTier = (members: number, deposit: string) =>
  api<PartnerTier>('/admin/partner-tiers', { method: 'POST', body: { members, deposit } })

/** XM-95 — 단계 줄 은퇴(새 신청·단계 올리기에 안 씀, 고른 마스터는 그대로 — 확인 31). */
export const retirePartnerTier = (tierId: number) =>
  api<PartnerTier>(`/admin/partner-tiers/${seg(tierId)}/retire`, { method: 'POST' })

/* ── 5-5 보존·파기·보관 파일 ──────────────────────────────────────── */

/** XM-76 — 회원 로그 주 단위 보관 파일 목록. */
export const listUserLogArchives = () => api<UserLogArchive[]>('/admin/user-log-archives')

/** XM-77 — 파기 정책 목록. */
export const listRetentionPolicies = () => api<RetentionPolicy[]>('/admin/retention/policies')

/**
 * XM-78 — 파기 정책 바꾸기(바뀐 칸만). 보존 햇수는 5 이상(서버가 400 으로 막는다).
 * ⚠ 켜기만으로는 아무것도 지우지 않는다 — 세기(XM-79) 뒤 사람이 승인(XM-81)해야 지운다.
 */
export const patchRetentionPolicy = (target: string, patch: { enabled?: boolean; retentionYears?: number }) =>
  api<RetentionPolicy>(`/admin/retention/policies/${seg(target)}`, { method: 'PATCH', body: patch })

/** XM-79 — 지금 지울 대상을 세어 승인 대기 실행을 만든다(지우지 않는다). */
export const previewRetention = (target: string) =>
  api<RetentionRun>(`/admin/retention/policies/${seg(target)}/preview`, { method: 'POST' })

/** XM-80 — 파기 실행 목록(page·size, 배열 답 — 새것부터). */
export const listRetentionRuns = (p: { target?: Opt<string> } & PageParams = {}) =>
  api<RetentionRun[]>('/admin/retention/runs' + query({ ...p }))

/** XM-81 — 파기 승인. ⚠ **되돌릴 수 없다** — 기준 시각보다 오래된 기록을 지운다. */
export const approveRetentionRun = (id: number) =>
  api<RetentionRun>(`/admin/retention/runs/${seg(id)}/approve`, { method: 'POST' })

/** XM-82 — 파기 거절. */
export const rejectRetentionRun = (id: number) =>
  api<RetentionRun>(`/admin/retention/runs/${seg(id)}/reject`, { method: 'POST' })

/* ── 연 단위 보관 (XM-97~XM-99 — R-XB-26·R-XAD-06) ─────────────────── */

/**
 * XM-97 — 연 단위 보관 현황: 운영 DB 에 두는 해(올해 + 작년), 보관할 수 있는 마지막 해(올해 − 2),
 * 함께 옮기는 표 묶음, 최근 보관 실행(표별 행 수·md5). 키 `retention.view`.
 */
export const getArchiveOverview = () => api<ArchiveOverview>('/admin/archives')

/**
 * XM-98 — 그 해 기록을 지금 보관소(`archive` 스키마)로 옮긴다(매일 작업과 같은 일). 키 `retention.edit`.
 * 올해·작년이거나 그 해 보관이 진행 중이면 409. 묶음 하나가 어긋나면 그 묶음은 되돌리고 실행은
 * `FAILED` 로 답한다(200). 지우지 않는다 — 보관된 행의 파기는 XM-77~XM-82 승인 절차.
 */
export const archiveYear = (year: number) =>
  api<ArchiveRun>(`/admin/archives/${seg(year)}`, { method: 'POST' })

/** XM-99 — 보관소의 그 해 그 표 행(page 0부터, size 1~200 — 기본 50). 키 `retention.view`. */
export const listArchivedRows = (year: number, p: { table: string } & PageParams) =>
  api<ArchivedRows>(`/admin/archives/${seg(year)}/rows` + query({ ...p }))
